import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useRouteMatch } from 'react-router-dom';
import classnames from 'classnames';
import { Preloader } from '../../../../components/Preloader';
import { Button } from '../Button';
import { ArrowLeftSmallIcon } from '../../icons/ArrowLeftSmall';
import MainInfo from './MainInfo';
import CourseInfo from './CourseInfo';
import MainInfoExtra from './MainInfoExtra';
import NextPreview from './NextPreview';
import { calcProgress } from './utils/calcProgress';
import { getCourse } from '../../api/getCourse';
import { PopupAccess } from '../../../../components/PopupAccess';
import { CardsContext } from '../../CardsContext';
import * as cl from './Course.module.scss';
import { sendPageViewEvent } from '../../../../utils/utils';
import AppLink from '../../../../components/AppLink';
import PlaySVG from './SVG/PlaySVG';
import { LockIcon } from '../../icons/Lock';
import CheckSVG from './SVG/CheckSVG';
import { getCourseLesson } from '../../api/getCourseLesson';


export function Course({ name, onBack }) {
  const { url } = useRouteMatch();
  const { routes } = useContext(CardsContext);
  const [isLoading, setIsLoading] = useState(true);
  const [state, setState] = useState(null);
  const [error, setError] = useState(null);
  const [simpleStructure, setSimpleStructure] = useState(true);
  const [nextBlock, setNextBlock] = useState(null);
  const [currentBlockName, setCurrentBlockName] = useState(null);
  const [isShowProgress, setIsShowProgress] = useState(false);
  const [progressInPercent, setProgressInPercent] = useState([]);
  const [currentAccess, setCurrentAccess] = useState(null);
  const [isShowPopupAccess, setIsShowPopupAccess] = useState(false);
  const courseUrl = url.replace(/\/+$/, '');

  const load = useCallback(async (name) => {
    try {
      setIsLoading(true);

      const data = await getCourse(name);

      if (data?.data && !Array.isArray(data?.data)) {
        const blocks = Object.values(data?.data);
        const nextBlockResult = blocks[1] || null;

        setProgressInPercent(calcProgress(data.data));
        setSimpleStructure(false);
        setIsShowProgress(true);
        setNextBlock(nextBlockResult);
        setCurrentBlockName(blocks[0].title);
      }

      const parser = new DOMParser();
      const doc = parser.parseFromString(
        `${data.meta.title} – PokerCoaching.com`,
        'text/html'
      );
      document.title = doc.body.innerText;

      let preparedData = data;
      if (data.meta.quiz_only === true) {
        try {
          const promises = [];
          data.data.forEach((lesson) => {
            promises.push(getCourseLesson(lesson.post_name));
          });
          const response = await Promise.all(promises);
          const dataWithLessons = data.data.reduce((acc, curr) => {
            const lessonData = response.find(
              (item) => item.lesson_id === curr.id
            );
            console.log(lessonData, 'lesson data');
            return [...acc, { ...curr, ld_quizzes: lessonData['ld-quizzes'] }];
          }, []);
          preparedData = { meta: data.meta, data: dataWithLessons };
        } catch (e) {
          console.log(e);
        }
      }

      setState(preparedData);
      sendPageViewEvent(data.meta.title);
    } catch (e) {
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load(name);
  }, [load, name]);

  function changeSelect(choice) {
    // actid=13
    window.analytics?.track('object:click', {
      object: 'topic',
      parent_object_title: 'course_topic_list',
      course_title: name,
      topic_title: choice,
      title: choice,
      url: url + choice.post_name,
    });

    const nextBlockIndex =
      Object.values(state.data).findIndex((el) => el.title === choice) + 1;
    const nextBlockResult = Object.values(state.data)[nextBlockIndex] || null;

    setNextBlock(nextBlockResult);
    setCurrentBlockName(choice);
  }

  const handleNoAccess = useCallback((access) => {
    const loginModal = document.querySelector('.pc-modal-login');
    loginModal.classList.add('show');
    setCurrentAccess(access);
  }, []);

  const handleEnroll = useCallback(
    (responce) => {
      if (!responce.success) {
        return;
      }

      load(name);
    },
    [load, name]
  );

  if (isLoading) {
    return <Preloader className={cl.preloader} />;
  }

  if (error) {
    return (
      <div className="error">
        <p className="error__msg">{error}</p>
        <AppLink to={`${routes.home}`} className="error__btn">
          Courses
        </AppLink>
      </div>
    );
  }

  const isLocked = state.meta.locked;
  const isEnroll = state.meta.need_to_enroll;

  const renderData = () => {
    if (state.meta.quiz_only == true) {
      return (
        <div className={cl.quizListWrapper}>
          {state.data.map((item) => {
            return (
              <div className={cl.quizListInner}>
                <div className={cl.quizListTitle}>{item.title}</div>
                <div className={cl.quizList}>
                  {item.ld_quizzes?.map((quiz) => {
                    return (
                      <div className={cl.quizListItem}>
                        <i style={{ marginRight: '12px' }}>
                          {item.has_access ? (
                            <PlaySVG />
                          ) : (
                            <LockIcon color="#8898AE" />
                          )}
                        </i>

                        <AppLink
                          to={`${courseUrl}/${item.post_name}/quizz/${quiz.post_name}/`}
                        >
                          <div
                            dangerouslySetInnerHTML={{ __html: quiz.title }}
                            className={cl.quizListItemTitle}
                          />
                        </AppLink>
                        {!!quiz?.completed && (
                          <i className={cl.quizListItemDone}>
                            <CheckSVG />
                          </i>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      );
    }
    return simpleStructure ? (
      <MainInfo
        state={state.data}
        meta={state.meta}
        name={name}
        locked={isLocked}
        enroll={isEnroll}
        changeSelect={changeSelect}
        onNoAccessClick={handleNoAccess}
        onEnroll={handleEnroll}
      />
    ) : (
      <>
        <MainInfoExtra
          state={state.data}
          meta={state.meta}
          name={name}
          currentBlockName={currentBlockName}
          changeSelect={changeSelect}
          locked={isLocked}
          enroll={isEnroll}
          onNoAccessClick={handleNoAccess}
          onEnroll={handleEnroll}
        />
        {!isLocked && nextBlock && (
          <NextPreview nextBlock={nextBlock} onNextClick={changeSelect} />
        )}
      </>
    );
  };

  return (
    <div
      className={classnames(cl.wrapper, {
        [cl.wrapperWide]: state.meta.content_wide,
      })}
    >
      <section className={cl.info}>
        <div>
          <Button className={cl.backBtn} type="link" onClick={onBack}>
            <span>
              <span className="anticon">
                <ArrowLeftSmallIcon />
              </span>{' '}
              Courses
            </span>
          </Button>

          {renderData()}
        </div>

        {state?.meta && (
          <CourseInfo
            meta={state.meta}
            isShowProgress={isShowProgress}
            currentBlockName={currentBlockName}
            changeSelect={changeSelect}
            list={state.data}
            progressInPercent={progressInPercent}
            locked={state.meta.locked}
          />
        )}
      </section>

      {isShowPopupAccess && (
        <PopupAccess
          redirectionObjectTitle={state.meta.title}
          access={currentAccess}
          enrollProduct={state.meta.enroll_product}
          closeModal={() => setIsShowPopupAccess(false)}
        />
      )}
    </div>
  );
}
